"use client";

import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Loader2, Mail } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useMaintenanceMode } from '@/hooks/useMaintenanceMode';
import { MFAVerification } from '@/components/auth/MFAVerification';
import { MFABackupCodeVerification } from '@/components/auth/MFABackupCodeVerification';
import { PublicHeader } from '@/components/marketing/PublicHeader';
import { PublicFooter } from '@/components/marketing/PublicFooter';
import { MobileWarningBanner } from '@/components/MobileWarningBanner';
import { supabase } from '@/integrations/supabase/client';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

// Helper function to get redirect path based on user role
const getRedirectPath = async (userId: string): Promise<string> => {
  const { data: roleData } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId)
    .order('assigned_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  
  return roleData?.role === 'admin' ? '/admin/dashboard' : '/dashboard';
};

export default function LoginPage() {
  const { login, resendVerificationEmail, checkMFARequired, verifyMFA, verifyMFABackupCode, isAuthenticated, session } = useAuth();
  const { isInMaintenance } = useMaintenanceMode();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmailVerificationError, setShowEmailVerificationError] = useState(false);
  const [isResendingVerification, setIsResendingVerification] = useState(false);
  const [showMFAVerification, setShowMFAVerification] = useState(false);
  const [showBackupCodeVerification, setShowBackupCodeVerification] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string>('');

  // Redirect fully authenticated users to dashboard
  // Note: Since AAL property is not available in this Supabase setup,
  // we allow the MFA verification flow to handle navigation
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (isAuthenticated && session) {
        const currentAAL = (session as any)?.aal;
        console.log('[LoginPage] Checking auth status on mount:', { isAuthenticated, currentAAL });
        
        // If user is authenticated and AAL is not explicitly aal1 (requiring MFA),
        // and they're not in the middle of MFA verification, redirect
        if (!showMFAVerification && !showBackupCodeVerification) {
          // Check if MFA is actually required
          const { required } = await checkMFARequired();
          if (!required) {
            console.log('[LoginPage] User authenticated without MFA requirement, redirecting based on role');
            const redirectPath = await getRedirectPath(session.user.id);
            navigate(redirectPath);
          }
        }
      }
    };
    
    checkAuthStatus();
  }, [isAuthenticated, session, navigate, showMFAVerification, showBackupCodeVerification]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear errors on change
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setShowEmailVerificationError(false);
    
    try {
      // Login - don't navigate yet
      const loginData = await login(formData.email, formData.password);
      
      console.log('[LoginPage] Login data received:', {
        hasSession: !!loginData.session,
        sessionAAL: (loginData.session as any)?.aal,
        userId: loginData.user?.id
      });
      
      // CRITICAL: Wait a moment for the session to be fully established
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if MFA is required based on session AAL level
      const { required, factors, currentAAL } = await checkMFARequired();
      
      console.log('[LoginPage] MFA check results:', { 
        required, 
        factorCount: factors.length, 
        currentAAL,
        willShowMFA: required && factors.length > 0
      });
      
      if (required && factors.length > 0) {
        console.log('[LoginPage] Setting MFA verification screen');
        // Show MFA verification screen - user needs to provide 2FA code
        setMfaFactorId(factors[0].id);
        setShowMFAVerification(true);
        setIsSubmitting(false);
        // CRITICAL: Do NOT navigate - return here
        return;
      }
      
      // No MFA required or session already at aal2 - allow navigation
      if (currentAAL === 'aal2' || !required) {
        console.log('[LoginPage] MFA not required, navigating based on role');
        
        // Track successful login
        analytics.track({
          name: ANALYTICS_EVENTS.USER_LOGGED_IN,
          category: 'acquisition',
          properties: {
            login_method: 'password',
          },
        });

        const redirectPath = await getRedirectPath(loginData.user!.id);
        navigate(redirectPath);
        return;
      } else {
        // This should never happen, but log if it does
        console.error('[LoginPage] Unexpected state: MFA required but not showing verification', {
          required,
          currentAAL,
          factorCount: factors.length
        });
      }
    } catch (error) {
      console.error('LoginPage caught error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Invalid email or password';
      
      // Check if it's an email verification error
      if (errorMessage.toLowerCase().includes('verify your email')) {
        console.log('Setting email verification error state');
        setShowEmailVerificationError(true);
        setErrors({ general: errorMessage });
      } else {
        console.log('Setting general error state');
        setErrors({ general: errorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFAVerify = async (code: string) => {
    try {
      // Verify MFA - if this succeeds, the user has passed 2FA
      await verifyMFA(mfaFactorId, code);
      
      console.log('[LoginPage] MFA verification successful - navigating based on role');
      
      // Get user ID from session and redirect based on role
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession?.user?.id) {
        const redirectPath = await getRedirectPath(currentSession.user.id);
        navigate(redirectPath);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleMFACancel = () => {
    setShowMFAVerification(false);
    setShowBackupCodeVerification(false);
    setMfaFactorId('');
  };

  const handleBackupCodeVerify = async (code: string) => {
    try {
      // Verify backup code - if this succeeds, the user has passed 2FA
      await verifyMFABackupCode(code);
      
      console.log('[LoginPage] Backup code verification successful - navigating based on role');
      
      // Get user ID from session and redirect based on role
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession?.user?.id) {
        const redirectPath = await getRedirectPath(currentSession.user.id);
        navigate(redirectPath);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      throw error;
    }
  };

  const handleUseBackupCode = () => {
    setShowMFAVerification(false);
    setShowBackupCodeVerification(true);
  };

  const handleBackToMFA = () => {
    setShowBackupCodeVerification(false);
    setShowMFAVerification(true);
  };

  const handleResendVerification = async () => {
    if (!formData.email) return;

    setIsResendingVerification(true);
    try {
      await resendVerificationEmail(formData.email);
      navigate(`/auth/verify-email?email=${encodeURIComponent(formData.email)}`);
    } catch (error) {
      setErrors({ 
        general: error instanceof Error ? error.message : 'Failed to resend verification email' 
      });
    } finally {
      setIsResendingVerification(false);
    }
  };

  const isFormValid = formData.email && formData.password;

  // Show backup code verification if needed
  if (showBackupCodeVerification) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />
        <main className="flex-1 flex items-center justify-center p-4">
          <MFABackupCodeVerification
            onVerify={handleBackupCodeVerify}
            onCancel={handleMFACancel}
            onBackToMFA={handleBackToMFA}
          />
        </main>
        <PublicFooter />
      </div>
    );
  }

  // Show MFA verification screen if required
  if (showMFAVerification) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />
        <main className="flex-1 flex items-center justify-center p-4">
          <MFAVerification
            onVerify={handleMFAVerify}
            onCancel={handleMFACancel}
            onUseBackupCode={handleUseBackupCode}
          />
        </main>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <PublicHeader />
      <MobileWarningBanner />
      <main className="flex-1 flex items-center justify-center p-4 pb-16">
        <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <span className="text-primary-foreground font-bold text-xl">R</span>
          </div>
          <CardTitle className="text-2xl font-bold">Welcome back</CardTitle>
          <CardDescription>
            Sign in to your procurement platform account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {isInMaintenance && (
            <Alert className="mb-6 bg-orange-50 dark:bg-orange-950/20 border-orange-200 dark:border-orange-800">
              <AlertDescription className="text-orange-800 dark:text-orange-200">
                System is in maintenance mode. Only administrators can access the platform.
              </AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {errors.general && !showEmailVerificationError && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                {errors.general}
              </div>
            )}

            {showEmailVerificationError && (
              <Alert variant="destructive">
                <Mail className="h-4 w-4" />
                <AlertDescription className="space-y-2">
                  <p>{errors.general}</p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={isResendingVerification}
                    className="text-sm font-medium underline hover:no-underline"
                  >
                    {isResendingVerification ? 'Sending...' : 'Resend verification email'}
                  </button>
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'border-destructive' : ''}
                disabled={isSubmitting}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={errors.password ? 'border-destructive' : ''}
                  disabled={isSubmitting}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-8 w-8 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isSubmitting}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="rememberMe"
                  checked={formData.rememberMe}
                  onCheckedChange={(checked) => handleInputChange('rememberMe', checked as boolean)}
                />
                <Label htmlFor="rememberMe" className="text-sm">
                  Remember me
                </Label>
              </div>
              <Link 
                to="/auth/forgot-password" 
                className="text-sm text-primary hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={!isFormValid || isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/auth/signup" className="text-primary hover:underline font-medium">
              Sign up
            </Link>
          </div>
        </CardContent>
        </Card>
      </main>
      <PublicFooter />
    </div>
  );
}
